import { v4 as uuidv4 } from 'uuid';
import {
    createLoadedGroupIdResolver,
    createLoadedGroupsData,
    GroupLike,
    LoadedGroupsData
} from './groupSyncUtils';

type LoadedGroupIdResolver = ReturnType<typeof createLoadedGroupIdResolver>;
type EpsCollections = Parameters<typeof createLoadedGroupIdResolver>[2];

export interface PreviewGroupResolverResult {
    groupsToLoad: LoadedGroupsData['groupsToLoad'];
    resolveGroupId: LoadedGroupIdResolver;
}

function resolvePageEpsIdFromGroup(group: GroupLike, groupsEps: GroupLike[] = []): GroupLike['pageEpsId'] | null {
    if (group.pageEpsId) {
        return group.pageEpsId;
    }

    const epsGroup = groupsEps.find((candidate) => candidate.uuid === group.uuid);

    return epsGroup?.pageEpsId || null;
}

function withResolvedPageEpsIds(groupsToLoad: GroupLike[] = [], groupsEps: GroupLike[] = []): GroupLike[] {
    return groupsToLoad.map((groupToLoad) => {
        const pageEpsId = resolvePageEpsIdFromGroup(groupToLoad, groupsEps);

        return pageEpsId
            ? {
                  ...groupToLoad,
                  pageEpsId
              }
            : groupToLoad;
    });
}

function filterGroupsByEpsUuids(
    sourceGroups: GroupLike[] = [],
    groupsEps: GroupLike[] = [],
    loadFromStep1 = false
): GroupLike[] {
    if (!loadFromStep1) {
        return sourceGroups;
    }

    const epsGroupUuids = new Set(groupsEps.map((group) => group?.uuid).filter(Boolean));

    return sourceGroups.filter((group) => !group?.uuid || epsGroupUuids.has(group.uuid));
}

function createLoadedPreviewGroups(
    sourceGroups: GroupLike[] = [],
    groupsEps: GroupLike[] = [],
    loadFromStep1 = false
): LoadedGroupsData {
    const filteredGroups = filterGroupsByEpsUuids(sourceGroups, groupsEps, loadFromStep1);
    const loadedGroupsData = createLoadedGroupsData(filteredGroups, loadFromStep1, () => uuidv4());

    return {
        ...loadedGroupsData,
        groupsToLoad: withResolvedPageEpsIds(loadedGroupsData.groupsToLoad, groupsEps)
    };
}

/**
 * Prepares preview groups and returns a resolver that maps element groupIds
 * from EPS or legacy values to the loaded preview group ids.
 */
export function createGroupResolver(
    elementsGroups: GroupLike[] = [],
    groupsEps: GroupLike[] = [],
    listEPS: EpsCollections = [],
    loadFromStep1 = false
): PreviewGroupResolverResult {
    const { groupsToLoad, groupsMapping, groupReferenceToLoadedId } = createLoadedPreviewGroups(
        elementsGroups,
        groupsEps,
        loadFromStep1
    );

    return {
        groupsToLoad,
        resolveGroupId: createLoadedGroupIdResolver(
            groupsToLoad,
            groupsEps,
            listEPS,
            groupsMapping,
            groupReferenceToLoadedId
        )
    };
}
